import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, ArrowRight, FlaskConical, Scan, HeartPulse, BrainCircuit, Wind, Phone, MessageCircle, Activity, Microscope, Stethoscope, Heart, Brain } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FloatingButtons from '../components/FloatingButtons';
import { companyInfo } from '../data/mock';
import { publicAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const iconMap = {
  FlaskConical: FlaskConical,
  Scan: Scan,
  HeartPulse: HeartPulse,
  BrainCircuit: BrainCircuit,
  Wind: Wind,
  Activity: Activity,
  Microscope: Microscope,
  Stethoscope: Stethoscope,
  Heart: Heart,
  Brain: Brain,
  // Map service names to appropriate icons
  Cardiology: Heart,
  Electrophysiology: Brain
};

// Animated Service Icon Component
const AnimatedServiceIcon = ({ icon: IconName, serviceName = "", className = "" }) => {
  // Check if there's a specific icon for this service name
  const Icon = iconMap[serviceName] || iconMap[IconName] || FlaskConical;
  
  return (
    <div className={`relative ${className}`}>
      {/* Pulsing background ring */}
      <div className="absolute inset-0 rounded-xl bg-[#007897]/20 animate-ping opacity-20" style={{ animationDuration: '2s' }} />
      {/* Rotating gradient border */}
      <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-[#007897] via-[#00a0af] to-[#3949ab] opacity-30 animate-spin" style={{ animationDuration: '8s' }} />
      {/* Icon container */}
      <div className="relative w-full h-full bg-gradient-to-br from-[#007897]/10 to-[#3949ab]/10 rounded-xl flex items-center justify-center group-hover:from-[#007897] group-hover:to-[#3949ab] transition-all duration-300">
        <Icon className="w-7 h-7 text-[#007897] group-hover:text-white transition-colors animate-pulse" style={{ animationDuration: '2s' }} />
      </div>
    </div>
  );
};

const ServicesPage = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await publicAPI.getServices();
        setServices(response.data);
      } catch (error) {
        console.error('Error fetching services:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchServices();
  }, []);

  const categories = ['all', ...new Set(services.map(s => s.category))];

  // Helper to get all test names from a service (handles nested sub_services)
  const getServiceTestNames = (service) => {
    const testNames = [];
    if (service.sub_services && service.sub_services.length > 0) {
      service.sub_services.forEach(sub => {
        if (sub.tests) {
          sub.tests.forEach(test => {
            testNames.push(typeof test === 'string' ? test : test.name);
          });
        }
      });
    }
    if (service.tests && service.tests.length > 0) {
      service.tests.forEach(test => {
        testNames.push(typeof test === 'string' ? test : test.name);
      });
    }
    return testNames;
  };

  const filteredServices = services.filter(service => {
    const testNames = getServiceTestNames(service);
    const matchesSearch = service.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         service.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         testNames.some(t => t.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = selectedCategory === 'all' || service.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-[#007897] to-[#006680]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center text-white">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Our Services</h1>
            <p className="text-blue-100 max-w-2xl mx-auto mb-8">
              Comprehensive diagnostic services with state-of-the-art technology and experienced professionals.
            </p>
            <div className="flex items-center justify-center gap-2 text-sm">
              <Link to="/" className="text-blue-200 hover:text-white">Home</Link>
              <span className="text-blue-300">/</span>
              <span>Services</span>
            </div>
          </div>
        </div>
      </section>

      {/* Search & Filter */}
      <section className="py-8 bg-white border-b sticky top-20 z-30">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="relative w-full md:w-96">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
              <Input
                type="text"
                placeholder="Search services or tests..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {categories.map(category => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    selectedCategory === category
                      ? 'bg-[#007897] text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {category === 'all' ? 'All Services' : category}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#007897]"></div>
            </div>
          ) : filteredServices.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600">No services found matching your criteria.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredServices.map((service) => {
                const Icon = iconMap[service.icon] || FlaskConical;
                // Support both sub_services (backend) and subServices (old mock)
                const subServices = service.sub_services || service.subServices || [];

                return (
                  <div
                    key={service.id}
                    className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 border border-gray-100 group"
                  >
                    <div className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <AnimatedServiceIcon icon={service.icon} serviceName={service.name} className="w-14 h-14" />
                        <span className="text-xs font-medium text-[#5c6bc0] bg-blue-50 px-3 py-1 rounded-full">
                          {service.category}
                        </span>
                      </div>

                      <h3 className="text-xl font-semibold text-gray-900 mb-3 group-hover:text-[#007897] transition-colors">
                        {service.name}
                      </h3>
                      
                      <p className="text-gray-600 text-sm mb-4">
                        {service.description}
                      </p>

                      {/* Categories count display */}
                      <div className="mb-4">
                        <span className="text-sm text-gray-500">
                          {subServices.length > 0 ? `${subServices.length} categories` : 'Specialized diagnostics'}
                        </span>
                      </div>

                      <Link to={`/services/${service.slug}`}>
                        <Button className="w-full bg-[#007897] hover:bg-[#006680] text-white">
                          View All Tests
                          <ArrowRight size={16} className="ml-2" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#007897] text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Need Help Choosing?</h2>
          <p className="text-blue-100 mb-8">
            Our team is here to help you select the right tests for your health needs.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="/contact">
              <Button size="lg" className="bg-white text-[#007897] hover:bg-blue-50 px-8">
                <Phone size={18} className="mr-2" />
                Contact Us
              </Button>
            </a>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingButtons />
    </div>
  );
};

export default ServicesPage;
